import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from "next";
import Head from "next/head";

import { RegionParlorsContainer } from "@/containers/RegionParlorsContainer";
import {
  buildRegionPageProps,
  RegionPageProps,
} from "@/utils/buildRegionPageProps";
import { listRegionPageRegions } from "@/utils/regionFacilities";
import { skipDevBuildInGetStaticPaths } from "@/utils/skipDevBuildInGetStaticPaths";

/**
 * 시군구 화장장 페이지. "수원 화장장" 처럼 시군구 + 화장장이 실제 검색어다.
 *
 * 화장장은 전국 63곳이라 시군구당 0~1곳이다. 관내 시설이 있는 곳만 만들면
 * 페이지가 40개도 안 남고, 그러면 실검색어 대부분을 놓친다. 대신 인근 화장장을
 * 거리순으로 붙여서 채운다(레퍼런스 memorial-map.kr 이 같은 구조다).
 * 그 사실은 히어로와 섹션 문구에서 밝힌다. 감추면 제목과 목록이 어긋나 보인다.
 *
 * 생성 대상을 listRegionPageRegions 로 두는 이유는 parlor, facility 와 같다.
 * 세 표면의 생성 대상이 갈리면 서로의 nearbyRegions 링크가 404 로 빠진다.
 */
export const getStaticPaths: GetStaticPaths = async () => {
  const skipBuildResult =
    skipDevBuildInGetStaticPaths() as GetStaticPathsResult;

  if (skipBuildResult) {
    return skipBuildResult;
  }

  return {
    paths: listRegionPageRegions().map((region) => ({
      params: { sido: region.sido, sigungu: region.sigungu },
    })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const sido = params?.sido;
  const sigungu = params?.sigungu;

  if (typeof sido !== "string" || typeof sigungu !== "string") {
    return { notFound: true };
  }

  const props = buildRegionPageProps(sido, sigungu, "crematorium");

  if (!props) {
    return { notFound: true };
  }

  return {
    props: {
      ...props,
      pagePublishedTime: "2026-08-19T00:00:00+09:00",
      // CDN_URL이 없는 환경에서 "undefined/ogimage.png"가 og:image로 나가지 않게 한다
      ...(process.env.NEXT_PUBLIC_CDN_URL
        ? { pageOgImage: `${process.env.NEXT_PUBLIC_CDN_URL}/ogimage.png` }
        : {}),
    },
  };
};

const RegionCrematoriumPage = ({ pageJsonLd, ...data }: RegionPageProps) => {
  return (
    <>
      {pageJsonLd && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: pageJsonLd }}
          />
        </Head>
      )}
      <RegionParlorsContainer {...data} focus="crematorium" />
    </>
  );
};

export default RegionCrematoriumPage;
